// Shared type scale for all highlight templates. Each template should
// pull display/body sizing from these tokens rather than picking ad-hoc
// font sizes, so concept-to-concept template switches don't produce
// visibly inconsistent typography.
//
// Pixel reference (assuming Tailwind defaults):
//   xs=12 sm=14 base=16 lg=18 xl=20 2xl=24 3xl=30 4xl=36 5xl=48
//   6xl=60 7xl=72 / arbitrary values noted inline.
//
// Titles are sized by `fitTitle` below, not by TYPE.

import { stripMath } from '@/lib/markdown/previewText';

export const TYPE = {
  // Subtitle directly beneath the cover title
  coverSubtitle: 'text-base md:text-lg', // 16 → 18
  // Paragraph or bulleted body copy
  bodyText: 'text-base md:text-lg', // 16 → 18
  // Top-bar labels (concept name, page indicator)
  topBar: 'text-xs md:text-sm', // 12 → 14
  // Generic caption text (footer / metadata)
  caption: 'text-xs md:text-sm', // 12 → 14
  // Page counter (e.g. "02/05")
  page: 'text-sm md:text-base tabular-nums', // 14 → 16
  // Thumbnail script/accent line (slightly larger to compensate for
  // tighter cap height in script faces)
  thumbScript: 'text-2xl md:text-3xl', // 24 → 30
} as const;

export const LEADING = {
  // Tight display leading for big titles
  display: 'leading-[0.95]',
  // Readable body leading
  body: 'leading-relaxed',
  // Snug leading for short captions
  caption: 'leading-snug',
} as const;

// Standard horizontal padding for slide content. Kept here so all
// templates align their gutters consistently.
export const PADDING = {
  slideX: 'px-10 md:px-14',
  slideTop: 'pt-10 md:pt-14',
  slideBottom: 'pb-10 md:pb-14',
} as const;

// Title size steps, largest first. Fixed sizes, never viewport breakpoints:
// a full slide is always laid out on the 800px design square and scaled to
// fit (HighlightsReader, Quick Bytes), so `md:` / `lg:` tracked the browser
// window rather than the card - a phone got a 36px title on the same 800px
// canvas a laptop gave 84px. Thumbnails have their own small design.
const TITLE_STEPS = {
  cover: ['text-[5.25rem]', 'text-7xl', 'text-6xl', 'text-5xl'], // 84 → 48
  body: ['text-7xl', 'text-6xl', 'text-5xl', 'text-4xl'], // 72 → 36
  thumb: ['text-2xl', 'text-xl', 'text-lg', 'text-base'], // 24 → 16
} as const;

export type TitleRole = keyof typeof TITLE_STEPS;

// Step down once the longest word, or the whole title, passes each limit.
// One long uppercase word ("SETTLEMENT") at 84px is wider than the slide's
// text column, and a word cannot wrap - it clipped to "SETTLEMEN".
const WORD_LIMITS = [8, 10, 13];
const LENGTH_LIMITS = [24, 40, 64];

function stepFor(value: number, limits: number[]): number {
  const step = limits.findIndex(limit => value <= limit);
  return step === -1 ? limits.length : step;
}

/**
 * The size class for a slide title: the largest step its longest word and
 * total length fit, plus `break-words` so a word longer than the column
 * still breaks instead of clipping. Pass the WHOLE title, also when a
 * template splits it into halves, so both halves share one size.
 */
export function fitTitle(title: string, role: TitleRole = 'cover'): string {
  const plain = stripMath(title).trim();
  const longest = Math.max(0, ...plain.split(/\s+/).map(w => w.length));
  const step = Math.max(
    stepFor(longest, WORD_LIMITS),
    stepFor(plain.length, LENGTH_LIMITS)
  );
  return `${TITLE_STEPS[role][step]} break-words`;
}
